import { useCallback, useEffect, useMemo, useState } from 'react';
import { CashFlowsFormWithDataDisplay } from 'features/drilldown/cashflows/CashFlows';
import type { useSummaryDataReturnValue } from 'features/drilldown/cashflows/CashFlows';
import { FilterValue, PillFilters } from 'features/common-elements';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import { RemittanceBackButton } from './RemittanceBackButton';
import { mockRemittanceCashflowFormValues } from './mockRemittanceData';
import { useParams } from 'react-router-dom';
import { remittanceFiltersConfig } from './remittanceFilters.config';
import { decodeDealIdFromParam } from './utils';
import { Button, DownloadDocumentButton } from 'ui-kit';
import { downloadExcelFromJson } from 'utils/excelUtils';
import toast from 'react-hot-toast';
import { Sidecar } from './Sidecar';
import { RemittanceMetrics } from './RemittanceMetrics';
import { RemittancePerformanceSummary } from './RemittancePerformanceSummary';
import { useGetDealRemittanceSummaries } from './getDealRemittanceSummaries_gql';
import { useDealWireDetails } from './getDealWireDetails_gql';
import { useGetDealMonthRemittances } from './getDealMonthRemittances_gql';
import { summariesByPaymentMonthToMonthlyTableFull } from './dealRemittanceSummariesToMonthlyTable';
import { useDealRemittanceCashflowProjection } from './useDealRemittanceCashflowProjection';
import { mergePastAndProjected } from './mergePastAndProjected';
import type { MonthlyTableCashFlowMonth } from 'features/drilldown/cashflows/gql/__generated__/MonthlyTableCashFlowMonth';

function buildPerformanceSummaryFromMonthly(
  monthlyCashFlows: MonthlyTableCashFlowMonth[],
  currentBalanceCents: number,
): useSummaryDataReturnValue['data'] {
  const principal = monthlyCashFlows.reduce((s, r) => s + Number(r.principal_cents), 0);
  const interest = monthlyCashFlows.reduce((s, r) => s + Number(r.interest_cents), 0);
  const servicing = monthlyCashFlows.reduce((s, r) => s + Number(r.servicing_cents), 0);
  const prepayments = monthlyCashFlows.reduce((s, r) => s + Number(r.prepayments_cents), 0);
  const defaults = monthlyCashFlows.reduce((s, r) => s + Number(r.defaults_cents), 0);
  const losses = monthlyCashFlows.reduce((s, r) => s + Number(r.losses_cents), 0);
  // Match the Rust cashflow service's `calculate_totals` formula
  // (cashflows/src/api/cashflows/mod.rs) so bank / credit union users see the same
  // Subtotal / Total on the remittance page as on the regular cashflow modeling page.
  // `defaults_minus_losses` is signed: positive = net loss-recovery, negative = realized
  // net loss. Rolling it into Subtotal means Principal + Interest + Prepayments + Losses
  // line up to the Subtotal row the way users read a waterfall.
  const defaultsMinusLosses = defaults - losses;
  const subtotal = principal + interest + prepayments + defaultsMinusLosses;
  const total = subtotal - servicing;
  // Use the caller-supplied "now" UPB for both unpaidBalance and current_balance_cents.
  // Using the merged series' last row would pick up the *final* projected balance (≈ 0
  // after full amortization), causing the Cashflow Matrix percentage column to divide
  // by zero and render `∞%`. The correct denominator is the last realized past-side
  // ending balance — what the buyer currently owns.
  return {
    cashFlows: {
      totals: {
        principal_cents: principal,
        interest_cents: interest,
        servicing_cents: servicing,
        prepayments_cents: prepayments,
        // Realized defaults net of loss recoveries. Per-row inputs come from the new
        // `_participation_cents` aggregates mapped in dealRemittanceSummariesToMonthlyTable.
        defaults_minus_losses_cents: defaultsMinusLosses,
        subtotal_cents: subtotal,
        total_cents: total,
      },
      monthlyCashFlows,
      price_yield_matrix: { spread_matrix: null, yield_matrix: null },
    },
    unpaidBalance: currentBalanceCents,
    originalBalance:
      monthlyCashFlows.length > 0 ? Number(monthlyCashFlows[0]!.starting_balance_cents) : 0,
    loanCount: 0,
    averageBalance: 0,
    netCoupon: 0,
    wa_servicing_rate: 0,
    averageAge: 0,
    averageMaturity: 0,
    fico: 0,
    dti: 0,
    ltv: 0,
    current_balance_cents: currentBalanceCents,
  };
}

export const RemittanceCashFlow = () => {
  const { dealId: rawDealId } = useParams<{ dealId?: string }>();
  const dealId = decodeDealIdFromParam(rawDealId);
  const { data: summariesResponse, loading, error } = useGetDealRemittanceSummaries(dealId);
  const summariesByPaymentMonth = summariesResponse?.summaries_by_payment_month ?? [];

  // Past months: actual summaries from approved remittance uploads, mapped at 100%
  // tape basis so the chart shows the traded pool's own cashflows (both buyer and
  // seller see the same numbers). Per-party views (Investor vs Servicer) are
  // decomposed by the sidecar off the same summaries.
  const pastMonthlyCashFlows = useMemo(
    () => summariesByPaymentMonthToMonthlyTableFull(summariesByPaymentMonth),
    [summariesByPaymentMonth],
  );

  // Forward projection: Rust cashflow service from the deal's latest approved upload.
  // The api resolver derives the upload_id server-side — we only pass dealId.
  //
  // We consume `fullMonthlyCashFlows` (new in cashflows#138 / api#796) so the projection
  // is pool-level, matching the past-side full-basis mapping above. `monthlyCashFlows`
  // (investor-share) is available too via the same hook for any future per-party UI.
  const {
    fullMonthlyCashFlows: projectedMonthlyCashFlows,
    loading: projectionLoading,
    error: projectionError,
  } = useDealRemittanceCashflowProjection(dealId ?? undefined);

  // Concatenate past + projected into a single series for the chart + monthly table.
  // Past rows keep their 1..P indexing; projected rows reindex to P+1..P+K.
  // `pastMonthCount` is threaded down to the Chart and MonthlyTable so the user sees
  // an explicit "Actual | Projected" divider / per-row label.
  const merged = useMemo(
    () => mergePastAndProjected(pastMonthlyCashFlows, projectedMonthlyCashFlows),
    [pastMonthlyCashFlows, projectedMonthlyCashFlows],
  );
  const monthlyCashFlows = merged.monthlyCashFlows;
  const pastMonthCount = merged.pastMonthCount;

  // "Now" UPB = buyer's investor-share balance at the hand-off between realized and
  // projected. Drives the percentage denominator in the Cashflow Matrix and matches the
  // UPB strip. Preference order:
  //   1. Most recent past-month ending balance (realized state).
  //   2. First projected-month starting balance (when no past data yet — deals that
  //      have no approved uploads still show a projection from the most recent tape).
  //   3. 0 (no data at all; the page renders its empty state in this case).
  // Without the fallback to projected[0].starting, a projection-only deal would hit the
  // same divide-by-zero that the ∞% fix targeted.
  const pastCurrentBalanceCents = useMemo(() => {
    if (pastMonthlyCashFlows.length > 0) {
      return Number(pastMonthlyCashFlows[pastMonthlyCashFlows.length - 1]!.ending_balance_cents);
    }
    if (projectedMonthlyCashFlows.length > 0) {
      return Number(projectedMonthlyCashFlows[0]!.starting_balance_cents);
    }
    return 0;
  }, [pastMonthlyCashFlows, projectedMonthlyCashFlows]);

  const performanceData = useMemo(
    () => buildPerformanceSummaryFromMonthly(monthlyCashFlows, pastCurrentBalanceCents),
    [monthlyCashFlows, pastCurrentBalanceCents],
  );

  const { dealRole, transferPercent, listingName } = useDealWireDetails(dealId ?? undefined);

  const availablePaymentMonths = useMemo(
    () =>
      [...summariesByPaymentMonth].map((s) => s.payment_month).sort((a, b) => b.localeCompare(a)),
    [summariesByPaymentMonth],
  );

  const [selectedPaymentMonth, setSelectedPaymentMonth] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (availablePaymentMonths.length === 0) {
      if (selectedPaymentMonth !== undefined) setSelectedPaymentMonth(undefined);
      return;
    }
    if (!selectedPaymentMonth || !availablePaymentMonths.includes(selectedPaymentMonth)) {
      setSelectedPaymentMonth(availablePaymentMonths[0]);
    }
  }, [availablePaymentMonths, selectedPaymentMonth]);

  const {
    item: dealMonthItem,
    loading: dealMonthLoading,
    refetch: refetchDealMonth,
  } = useGetDealMonthRemittances(dealId ?? undefined, selectedPaymentMonth);
  // Sidecar `RemittanceMetrics` surfaces actual wire amounts (Investor/Servicer Total
  // cash, Principal, Interest), NOT projected future flows. Build its totals from the
  // past-only series so including the forward projection doesn't inflate the numbers
  // the user settles wires against. The chart / monthly table keep using the merged
  // series because that's what a projection view is supposed to show.
  //
  // These totals are 100% tape (full) amounts. `RemittanceMetrics` does the
  // Investor/Servicer split by applying `transferPercent` to principal + interest; the
  // servicer keeps 100% of fees. Feeding it investor-share values would double-scale and
  // report a fraction of the already-fractional share.
  const totalsCents = useMemo(() => {
    if (summariesByPaymentMonth.length === 0) return undefined;
    const principal = summariesByPaymentMonth.reduce(
      (s, e) => s + Number(e.summary?.total_principal_full_cents ?? 0),
      0,
    );
    const interest = summariesByPaymentMonth.reduce(
      (s, e) => s + Number(e.summary?.total_interest_full_cents ?? 0),
      0,
    );
    const servicing = summariesByPaymentMonth.reduce(
      (s, e) => s + Number(e.summary?.total_fees_cents ?? 0),
      0,
    );
    const payments = summariesByPaymentMonth.reduce(
      (s, e) => s + Number(e.summary?.total_payments_full_cents ?? 0),
      0,
    );
    return {
      principal_cents: principal,
      interest_cents: interest,
      servicing_cents: servicing,
      total_cents: payments - servicing,
    };
  }, [summariesByPaymentMonth]);
  // Combined loading/error across past summaries + projection so the chart shell shows a
  // single consolidated state rather than flashing between partial datasets.
  const combinedLoading = loading || projectionLoading;
  const combinedError = error ?? projectionError ?? undefined;
  const useSummaryData = useCallback(
    (): useSummaryDataReturnValue => ({
      loading: combinedLoading,
      error: combinedError,
      data: performanceData,
    }),
    [combinedLoading, combinedError, performanceData],
  );

  const [filters, setFilters] = useState<FilterValue<typeof remittanceFiltersConfig>[]>([]);
  const [isSidecarOpen, setIsSidecarOpen] = useState(false);

  const handleDownloadExcel = useCallback(() => {
    if (!monthlyCashFlows.length) {
      toast.error('No data available for download.');
      return;
    }
    downloadExcelFromJson(monthlyCashFlows, 'Monthly Cashflows', 'RemittanceCashFlowSummary.xlsx');
  }, [monthlyCashFlows]);

  const showEmpty = !combinedLoading && !combinedError && dealId && monthlyCashFlows.length === 0;
  const showContent = dealId && (monthlyCashFlows.length > 0 || combinedLoading || combinedError);

  return (
    <PortfolioLayout
      hideSummery={true}
      summeryChildren={
        <div>
          <RemittanceBackButton />
          <div className="mt-1 flex items-start gap-6">
            <div className="flex-shrink-0">
              <span className="text-sm font-normal text-gray-400">Cashflow Profile for</span>
              <div className="mt-0.5 text-xl font-semibold text-white">
                {listingName ?? dealId ?? '…'}
              </div>
              {transferPercent < 100 && dealRole !== undefined && (
                <div className="mt-0.5 text-xs font-normal text-gray-400">
                  Your share:{' '}
                  <span className="text-gray-200">
                    {dealRole === 'BUYER' ? transferPercent : 100 - transferPercent}%
                  </span>{' '}
                  of pool
                </div>
              )}
            </div>
            {showContent && (
              <RemittancePerformanceSummary
                summariesByPaymentMonth={summariesByPaymentMonth}
                loading={loading}
                paymentMonth={
                  summariesByPaymentMonth.length > 0
                    ? [...summariesByPaymentMonth].sort((a, b) =>
                        b.payment_month.localeCompare(a.payment_month),
                      )[0]?.payment_month
                    : undefined
                }
              />
            )}
          </div>
          {!dealId && (
            <div className="mt-2 text-sm text-gray-400">
              No deal selected. Open a deal from Serviced deals and use View Cashflow Profile.
            </div>
          )}
          {combinedError && (
            <div className="mt-2 text-sm text-red-400" role="alert">
              Failed to load cashflow data: {combinedError.message}
            </div>
          )}
          {showEmpty && (
            <div className="mt-2 text-sm text-gray-400">
              No remittance summaries for this deal yet.
            </div>
          )}
          {showContent && (
            <div className="mt-2 flex items-center justify-between">
              <PillFilters
                filtersConfig={remittanceFiltersConfig}
                currentFilters={filters}
                setCurrentFilters={setFilters}
              />
              <div className="mt-2 flex">
                <DownloadDocumentButton
                  onClick={handleDownloadExcel}
                  fileType="excel"
                  tooltipText="Download Excel"
                  tooltipPosition="left"
                  className="mr-4"
                />
                <Button size="small" onClick={() => setIsSidecarOpen(true)}>
                  Open Remittance Metrics & Wire Information
                </Button>
              </div>
            </div>
          )}
        </div>
      }
    >
      {showContent && (
        <CashFlowsFormWithDataDisplay
          formValues={mockRemittanceCashflowFormValues}
          useSummaryData={useSummaryData}
          onSubmit={() => {}}
          hideModelingInputs={true}
          pastMonthCount={pastMonthCount}
        />
      )}
      <Sidecar
        isOpen={isSidecarOpen}
        onClose={() => setIsSidecarOpen(false)}
        widthPercent={35}
        header="Remittance Metrics & Wire Information"
      >
        <RemittanceMetrics
          totalsCents={totalsCents}
          transferPercent={transferPercent}
          dealRole={dealRole}
          dealId={dealId ?? undefined}
          dealMonthItem={dealMonthItem}
          dealMonthLoading={dealMonthLoading}
          availablePaymentMonths={availablePaymentMonths}
          selectedPaymentMonth={selectedPaymentMonth}
          onSelectPaymentMonth={setSelectedPaymentMonth}
          refetchDealMonth={() => {
            refetchDealMonth().catch((err: unknown) => {
              const msg = err instanceof Error ? err.message : String(err);
              toast.error(`Failed to refresh: ${msg || 'unknown error'}`);
            });
          }}
        />
      </Sidecar>
    </PortfolioLayout>
  );
};
